import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { toast } from "sonner";
import { Loader2, Save, ChevronLeft, ChevronRight, Info, Download, Upload, BedDouble, RotateCcw, Sparkles, DollarSign, ShieldOff } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import RateChangeWizard from "./RateChangeWizard";
import BaseRatesPanel from "./BaseRatesPanel";
import RestrictionsTab from "./RestrictionsTab";

const today10 = () => new Date().toISOString().slice(0, 10);
const plusDays = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);
const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function RatesTab({ pid, propertyName }) {
  const [view, setView] = useState("grid"); // grid | base
  const [wizardOpen, setWizardOpen] = useState(false);
  const [baseState, setBaseState] = useState(null); // {all_set, missing_count}
  const [start, setStart] = useState(today10());
  const [cal, setCal] = useState(null);
  const [edits, setEdits] = useState({}); // { date: rate }
  const [busy, setBusy] = useState(false);
  const [pool, setPool] = useState("OTA Common Pool");
  const [rateMode, setRateMode] = useState("base"); // base | adult | child
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [hideDerived, setHideDerived] = useState(true);
  const [roomTypeFilter, setRoomTypeFilter] = useState("");
  const [channelCount, setChannelCount] = useState(0);

  const startMonth = start.slice(0, 7);
  const load = useCallback(() => {
    setCal(null); setEdits({});
    api.get(`/pms/properties/${pid}/calendar`, { params: { month: startMonth } })
      .then(({ data }) => setCal(data))
      .catch((e) => toast.error(errMsg(e)));
  }, [pid, startMonth]);
  useEffect(() => { load(); }, [load]);

  // Poll base-rates snapshot so we can nudge the host if some units are unpriced
  const loadBase = useCallback(() => {
    api.get(`/pms/properties/${pid}/base-rates`).then(({ data }) => setBaseState(data)).catch(() => {});
    api.get(`/pms/overview`).then(({ data }) => {
      const prop = data.properties.find((p) => p.id === pid);
      setChannelCount(prop?.channel_count || 0);
    }).catch(() => {});
  }, [pid]);
  useEffect(() => { loadBase(); }, [loadBase]);

  const days = useMemo(() => {
    if (!cal) return [];
    const arr = [];
    for (let i = 0; i < 14; i++) {
      const iso = plusDays(start, i);
      const idx = new Date(iso + "T00:00:00").getDate() - 1;
      const rateRow = cal.rates[idx];
      arr.push({ iso, day: iso.slice(8), month: new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { month: "short" }), weekday: WD[new Date(iso + "T00:00:00").getDay()], rate: rateRow?.rate || cal.base_rate, weekend: rateRow?.weekend, overridden: rateRow?.overridden });
    }
    return arr;
  }, [cal, start]);

  // Sold counts per iso date
  const soldMap = useMemo(() => {
    const m = {};
    if (!cal) return m;
    for (const b of cal.bookings) {
      if (!b.unit_id) continue;
      const ci = new Date(b.checkin), co = new Date(b.checkout);
      for (let d = new Date(ci); d < co; d.setDate(d.getDate() + 1)) {
        const iso = d.toISOString().slice(0, 10);
        m[iso] = (m[iso] || 0) + 1;
      }
    }
    return m;
  }, [cal]);

  const unitBooked = useCallback((unitId, iso) => {
    if (!cal) return false;
    return cal.bookings.some((b) => b.unit_id === unitId && b.checkin <= iso && iso < b.checkout);
  }, [cal]);

  const rateFor = (iso) => (edits[iso] !== undefined ? edits[iso] : (days.find((x) => x.iso === iso)?.rate ?? cal.base_rate));
  const isEdited = (iso) => edits[iso] !== undefined;

  const setRate = (iso, val) => setEdits((e) => ({ ...e, [iso]: val }));
  const commit = (iso) => {
    const raw = edits[iso];
    if (raw === undefined) return;
    const parsed = parseFloat(String(raw).replace(/[^\d.]/g, ""));
    setEdits((e) => ({ ...e, [iso]: isNaN(parsed) ? "" : parsed }));
  };

  const save = async () => {
    const overrides = Object.entries(edits)
      .map(([date, r]) => ({ date, rate: parseFloat(r), inventory: cal.units.length }))
      .filter((o) => !isNaN(o.rate) && o.rate >= 0);
    if (!overrides.length) return;
    setBusy(true);
    try {
      const { data: r } = await api.put(`/pms/properties/${pid}/rates`, { overrides });
      toast.success(`${r.saved} night${r.saved > 1 ? "s" : ""} pushed to every connected OTA · ${propertyName || "this property"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const resetDay = async (iso) => {
    setBusy(true);
    try {
      await api.delete(`/pms/properties/${pid}/rates/${iso}`);
      toast.success(`Rate reset to default on ${iso}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!cal) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  const filteredUnits = roomTypeFilter ? cal.units.filter((u) => u.name.toLowerCase().includes(roomTypeFilter.toLowerCase())) : cal.units;
  const dirty = Object.keys(edits).length > 0;
  const baseMissing = baseState && !baseState.all_set;

  return (
    <div className="reveal space-y-3">
      {/* View toggle: Grid <-> Base Rate Setup */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex bg-slate-100 rounded-full p-1">
          <button data-testid="rc-view-grid" onClick={() => setView("grid")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${view === "grid" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            Rate Grid
          </button>
          <button data-testid="rc-view-base" onClick={() => setView("base")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5 ${view === "base" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            <DollarSign size={12} /> Base Rate Setup {baseMissing && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
          </button>
          <button data-testid="rc-view-restrictions" onClick={() => setView("restrictions")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5 ${view === "restrictions" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            <ShieldOff size={12} /> Stopsells & Restrictions
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {baseMissing && view !== "base" && (
            <button data-testid="rc-fix-base" onClick={() => setView("base")}
              className="text-[11px] px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-semibold hover:bg-amber-100">
              {baseState.units.filter((u) => !u.is_set).length} unit{baseState.units.filter((u) => !u.is_set).length === 1 ? "" : "s"} without a base rate →
            </button>
          )}
          <button data-testid="rc-open-wizard" onClick={() => setWizardOpen(true)}
            className="btn-brand rounded-full px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5">
            <Sparkles size={12} /> Rate Change Wizard
          </button>
        </div>
      </div>

      {view === "base" && (
        <BaseRatesPanel pid={pid} propertyName={propertyName} connectedChannels={channelCount}
          onSaved={() => { loadBase(); load(); }} />
      )}

      {view === "restrictions" && (
        <RestrictionsTab pid={pid} month={startMonth} />
      )}

      {view === "grid" && (
      <>
      {/* Sub-tab strip */}
      <div className="flex items-center gap-1 border-b border-slate-200 flex-wrap">
        {["Inventory", "Rates", "Minimum Nights", "Maximum Nights", "Stopsells", "COA", "COD"].map((t) => (
          <button key={t} data-testid={`rc-sub-${t}`}
            className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${t === "Rates" ? "border-[#4F46E5] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <input ref={(el) => (window.__rcImport = el)} type="file" accept=".csv" className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              const text = await file.text();
              const lines = text.split(/\r?\n/).filter(Boolean);
              const [header, ...rows] = lines.map((l) => l.split(","));
              const dates = header.slice(1);
              const nextEdits = { ...edits };
              const patches = [];
              rows.forEach((row) => {
                const uName = row[0];
                const unit = (cal?.units || []).find((u) => u.name === uName);
                if (!unit) return;
                row.slice(1).forEach((rate, i) => {
                  const r = parseFloat(rate);
                  if (!isNaN(r) && r > 0) patches.push({ unit_id: unit.id, date: dates[i], rate: r });
                });
              });
              patches.forEach((p) => { nextEdits[`${p.unit_id}|${p.date}`] = p.rate; });
              setEdits(nextEdits);
              toast.success(`Imported ${patches.length} cells — review & Save`);
              e.target.value = "";
            }} />
          <button data-testid="rc-import" onClick={() => window.__rcImport?.click()}
            className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg text-slate-600 hover:border-slate-400 flex items-center gap-1.5"><Upload size={12} /> Import</button>
          <button data-testid="rc-export" onClick={() => {
            if (!cal?.units || !cal?.days) return toast.error("Nothing to export yet");
            const rateMap = {}; (cal.rates || []).forEach((r) => { rateMap[`${r.unit_id}|${r.date}`] = r.rate; });
            const rows = [["Unit", ...cal.days.map((d) => d.iso)]];
            cal.units.forEach((u) => rows.push([u.name, ...cal.days.map((d) => rateMap[`${u.id}|${d.iso}`] ?? cal.city_default ?? "")]));
            const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
            a.download = `${propertyName || "rates"}-${startMonth}.csv`; a.click();
            toast.success("CSV downloaded");
          }} className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg text-slate-600 hover:border-slate-400 flex items-center gap-1.5"><Download size={12} /> Export</button>
          <span className="text-[10px] text-slate-500 flex items-center gap-1"><Info size={11} /> Rates are in ₹ and are Tax Exclusive</span>
        </div>
      </div>

      {/* Filter row */}
      <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-4 flex-wrap">
        <select value={pool} onChange={(e) => setPool(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
          <option>OTA Common Pool</option>
          <option>Atiti</option>
          <option>Booking.com</option>
          <option>Airbnb</option>
          <option>MakeMyTrip</option>
        </select>
        <input placeholder="Filter unit type…" value={roomTypeFilter} onChange={(e) => setRoomTypeFilter(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none min-w-[180px]" />
        {[["base", "Base Rates"], ["adult", "Extra Adult Rates"], ["child", "Extra Child Rates"]].map(([id, l]) => (
          <label key={id} className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer">
            <input type="radio" name="rc-mode" value={id} checked={rateMode === id} onChange={() => setRateMode(id)} />
            {l}
          </label>
        ))}
        <label className="text-xs text-slate-600 flex items-center gap-1.5 ml-auto"><input type="checkbox" checked={hideDerived} onChange={(e) => setHideDerived(e.target.checked)} /> Hide Derived Rate Plans</label>
        <label className="text-xs text-slate-600 flex items-center gap-1.5"><input type="checkbox" checked={taxInclusive} onChange={(e) => setTaxInclusive(e.target.checked)} /> Rates Inclusive Tax</label>
        <button data-testid="rates-save" onClick={save} disabled={!dirty || busy}
          className="btn-brand rounded-lg px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save
        </button>
      </div>

      {/* Grid */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="border-collapse text-sm" style={{ minWidth: 260 + 14 * 96 }}>
          <thead>
            <tr>
              <th className="sticky left-0 bg-white z-10 w-[260px] p-2 border-b border-r border-slate-200 text-left">
                <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500">
                  <button data-testid="rc-prev" onClick={() => setStart(plusDays(start, -14))} className="w-7 h-7 border border-slate-200 rounded-lg hover:border-slate-400 flex items-center justify-center"><ChevronLeft size={13} /></button>
                  <input data-testid="rc-start" type="date" value={start} onChange={(e) => setStart(e.target.value || today10())} className="border border-slate-200 rounded-lg px-2 py-1 text-xs" />
                  <button data-testid="rc-next" onClick={() => setStart(plusDays(start, 14))} className="w-7 h-7 border border-slate-200 rounded-lg hover:border-slate-400 flex items-center justify-center"><ChevronRight size={13} /></button>
                </div>
              </th>
              {days.map((d) => {
                const isToday = d.iso === today10();
                return (
                  <th key={d.iso} className={`w-24 p-1 border-b border-r border-slate-100 ${d.weekend ? "bg-amber-50/40" : ""} ${isToday ? "bg-[#EEF2FF]/60" : ""}`}>
                    <div className={`text-[10px] font-semibold uppercase tracking-wider ${isToday ? "brand-pink" : "text-slate-400"}`}>{d.weekday}</div>
                    <div className={`text-[13px] font-display font-black ${isToday ? "brand-pink" : "text-slate-800"}`}>{d.day}</div>
                    <div className="text-[9px] font-semibold text-slate-400 uppercase">{d.month}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredUnits.map((u) => (
              <Fragment key={u.id}>
                {/* Unit type row (inventory) */}
                <tr>
                  <td className="sticky left-0 bg-white z-10 px-3 py-2 border-b border-r border-slate-100">
                    <div className="flex items-center gap-2">
                      <BedDouble size={13} className="text-slate-400 shrink-0" />
                      <span className="font-display font-bold text-slate-800 text-[13px] truncate">{u.name}</span>
                      <span className="ml-auto text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 rounded-full">1</span>
                    </div>
                  </td>
                  {days.map((d) => {
                    const booked = unitBooked(u.id, d.iso);
                    return (
                      <td key={d.iso} className={`text-center py-1.5 border-b border-r border-slate-100 ${d.weekend ? "bg-amber-50/20" : ""}`}>
                        <span className={`text-[13px] font-bold ${booked ? "text-red-500" : "text-slate-700"}`}>{booked ? 0 : 1}</span>
                      </td>
                    );
                  })}
                </tr>
                {/* Rate row */}
                <tr>
                  <td className="sticky left-0 bg-white z-10 px-3 py-1.5 border-b border-r border-slate-100">
                    <p className="text-[11px] text-slate-600 truncate">{u.name} <span className="text-slate-400">· Base rate</span></p>
                  </td>
                  {days.map((d) => {
                    const rate = rateFor(d.iso);
                    const edited = isEdited(d.iso);
                    return (
                      <td key={d.iso} className={`py-1 border-b border-r border-slate-100 ${d.weekend ? "bg-amber-50/20" : ""} ${edited ? "bg-[#EEF2FF]/50" : ""}`}>
                        <div className="relative px-1">
                          <input data-testid={`rc-cell-${u.id}-${d.iso}`}
                            type="text" inputMode="decimal"
                            value={typeof rate === "number" ? fmt(rate) : rate}
                            onChange={(e) => setRate(d.iso, e.target.value.replace(/[^\d.]/g, ""))}
                            onBlur={() => commit(d.iso)}
                            onKeyDown={(e) => { if (e.key === "Enter") { commit(d.iso); save(); } }}
                            className={`w-full border rounded px-2 py-1.5 text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 ${d.overridden || edited ? "border-[#4F46E5] text-[#4F46E5] font-bold" : "border-slate-200 text-slate-700"}`} />
                          {d.overridden && !edited && (
                            <button data-testid={`rc-reset-${d.iso}`} onClick={() => resetDay(d.iso)} title="Reset to default" className="absolute -right-0.5 -top-0.5 text-slate-300 hover:text-red-500 text-[10px] leading-none">×</button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            ))}
          </tbody>
          <tfoot className="bg-slate-50/60">
            <tr>
              <td className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-[10px] uppercase font-semibold tracking-wider text-slate-500 border-t border-r border-slate-200">Sold Units</td>
              {days.map((d) => <td key={d.iso} className={`text-center py-2 border-t border-r border-slate-100 text-[11px] font-mono font-bold text-slate-600 ${d.weekend ? "bg-amber-50/20" : ""}`}>{soldMap[d.iso] || 0}</td>)}
            </tr>
            <tr>
              <td className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-[10px] uppercase font-semibold tracking-wider text-slate-500 border-r border-slate-200">Available Inventory</td>
              {days.map((d) => <td key={d.iso} className={`text-center py-2 border-r border-slate-100 text-[11px] font-mono font-bold ${d.weekend ? "bg-amber-50/20" : ""}`} style={{ color: (cal.units.length - (soldMap[d.iso] || 0)) === 0 ? "#EF4444" : "#0F172A" }}>{cal.units.length - (soldMap[d.iso] || 0)}</td>)}
            </tr>
            <tr>
              <td className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-[10px] uppercase font-semibold tracking-wider text-slate-500 border-r border-slate-200">Total Units</td>
              {days.map((d) => <td key={d.iso} className={`text-center py-2 border-r border-slate-100 text-[11px] font-mono font-bold text-slate-800 ${d.weekend ? "bg-amber-50/20" : ""}`}>{cal.units.length}</td>)}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">Any rate change here pushes to Atiti + every connected OTA in a single call. Weekend columns are tinted amber; overridden rates render pink with an × to reset.</p>
      </>
      )}

      {wizardOpen && (
        <RateChangeWizard pid={pid} propertyName={propertyName}
          units={(baseState?.units || cal.units.map((u) => ({ unit_id: u.id, name: u.name, base_rate: u.base_rate, suggested_rate: baseState?.city_default }))).map((u) => ({ id: u.unit_id || u.id, name: u.name, base_rate: u.base_rate, suggested_rate: u.suggested_rate }))}
          cityDefault={baseState?.city_default || cal.base_rate}
          connectedChannels={channelCount}
          onClose={() => setWizardOpen(false)}
          onApplied={() => { load(); loadBase(); }} />
      )}
    </div>
  );
}
